<script lang="ts">
  import { writeWav, type WavEncoding } from '../codecs/wav';
  import { peakAbs, rmsDbfs, ampToDb } from '../dsp/levels';
  import { session } from '../state/session.svelte';
  import GeneratorDialog from './GeneratorDialog.svelte';
  import SimpleWaveform from './plots/SimpleWaveform.svelte';
  import { downloadBytes, safeFilename } from './download';
  import { formatDb, formatInt, formatTime } from './format';

  let generatorOpen = $state(false);

  const doc = $derived(session.doc);
  const ch0 = $derived(doc?.channels[0]);
  const stats = $derived(ch0 ? { peak: ampToDb(peakAbs(ch0)), rms: rmsDbfs(ch0) } : null);

  function exportWav(enc: WavEncoding) {
    if (!doc) return;
    const bytes = writeWav(doc.channels, doc.sampleRate, enc);
    downloadBytes(bytes, `${safeFilename(doc.name)}.wav`, 'audio/wav');
  }
</script>

<section class="panel toolbar" data-testid="view-analysis">
  <h2>Analysis</h2>
  <div class="actions">
    <button class="primary" onclick={() => (generatorOpen = true)} data-testid="new-generate">
      New ▸ Generate…
    </button>
    <button disabled title="Arrives in M3">Open file…</button>
  </div>
</section>

{#if doc && ch0}
  <section class="panel" data-testid="doc-info">
    <h3 data-testid="doc-name">{doc.name}</h3>
    <dl>
      <dt>Duration</dt>
      <dd data-testid="doc-duration">{formatTime(ch0.length / doc.sampleRate)}</dd>
      <dt>Sample rate</dt>
      <dd data-testid="doc-rate">{doc.sampleRate} Hz</dd>
      <dt>Samples</dt>
      <dd data-testid="doc-samples">{formatInt(ch0.length)}</dd>
      <dt>Channels</dt>
      <dd>{doc.channels.length}</dd>
      {#if stats}
        <dt>Peak level</dt>
        <dd>{formatDb(stats.peak)}</dd>
        <dt>RMS level</dt>
        <dd>{formatDb(stats.rms)} <span class="hint">(full-scale sine = 0 dBFS)</span></dd>
      {/if}
    </dl>
    <SimpleWaveform samples={ch0} sampleRate={doc.sampleRate} />
    <div class="export">
      Export WAV:
      <button onclick={() => exportWav('pcm16')} data-testid="export-wav16">16-bit</button>
      <button onclick={() => exportWav('pcm24')}>24-bit</button>
      <button onclick={() => exportWav('float32')}>32-bit float</button>
    </div>
    <p class="hint">Frame-by-frame viewer, zoom and playback arrive in milestone M3.</p>
  </section>
{:else}
  <section class="panel empty">
    <p>
      Nothing open yet. Click <strong>New ▸ Generate…</strong> to create a test signal (sine, tones, noise,
      sweeps, clicks…).
    </p>
  </section>
{/if}

<GeneratorDialog bind:open={generatorOpen} />

<style>
  .toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
  }
  h2,
  h3 {
    margin: 0;
  }
  .actions {
    display: flex;
    gap: 0.5rem;
  }
  dl {
    display: grid;
    grid-template-columns: max-content 1fr;
    gap: 0.2rem 1rem;
    margin: 0.75rem 0 1rem;
  }
  dt {
    color: var(--muted);
  }
  dd {
    margin: 0;
    font-family: var(--mono);
  }
  .export {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.75rem;
    flex-wrap: wrap;
  }
  .hint {
    color: var(--muted);
    font-size: 0.85rem;
    font-family: var(--font);
  }
  .empty {
    color: var(--muted);
  }
</style>
